import { useState } from 'react';
import styles from './ClienteModal.module.css';

// ====================================================================
// MÁSCARAS
// ====================================================================

function mascaraCNPJ(valor) {
  const nums = valor.replace(/\D/g, '').slice(0, 14);
  return nums
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2');
}

function mascaraCEP(valor) {
  const nums = valor.replace(/\D/g, '').slice(0, 8);
  return nums.replace(/^(\d{5})(\d)/, '$1-$2');
}

// Valida formato do CNPJ (00.000.000/0000-00) — só formato, sem dígito verificador
function cnpjFormatoValido(cnpj) {
  return /^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/.test(cnpj);
}

// ====================================================================
// COMPONENTE
// ====================================================================

export default function ClienteModal({ cliente, onSalvar, onCancelar }) {
  const editando = !!cliente;

  const [razaoSocial, setRazaoSocial] = useState(cliente?.razaoSocial || '');
  const [nomeFantasia, setNomeFantasia] = useState(cliente?.nomeFantasia || '');
  const [cnpj, setCnpj] = useState(cliente?.cnpj || '');
  const [inscricaoEstadual, setInscricaoEstadual] = useState(
    cliente?.inscricaoEstadual || ''
  );

  const [logradouro, setLogradouro] = useState(cliente?.endereco?.logradouro || '');
  const [numero, setNumero] = useState(cliente?.endereco?.numero || '');
  const [bairro, setBairro] = useState(cliente?.endereco?.bairro || '');
  const [cidade, setCidade] = useState(cliente?.endereco?.cidade || '');
  const [uf, setUf] = useState(cliente?.endereco?.uf || '');
  const [cep, setCep] = useState(cliente?.endereco?.cep || '');

  const [contatoNome, setContatoNome] = useState(cliente?.contato?.nome || '');
  const [contatoCargo, setContatoCargo] = useState(cliente?.contato?.cargo || '');
  const [contatoTelefone, setContatoTelefone] = useState(
    cliente?.contato?.telefone || ''
  );
  const [contatoEmail, setContatoEmail] = useState(cliente?.contato?.email || '');

  const [observacoes, setObservacoes] = useState(cliente?.observacoes || '');
  const [ativo, setAtivo] = useState(cliente?.ativo ?? true);

  // Validação: razão social + CNPJ no formato correto
  const cnpjOk = cnpj.length === 0 || cnpjFormatoValido(cnpj);
  const valido =
    razaoSocial.trim().length > 0 && cnpj.length > 0 && cnpjFormatoValido(cnpj);

  function handleSalvar() {
    if (razaoSocial.trim().length === 0) {
      alert('Preencha a razão social.');
      return;
    }
    if (!cnpjFormatoValido(cnpj)) {
      alert('CNPJ inválido. Use o formato 00.000.000/0000-00');
      return;
    }
    onSalvar({
      razaoSocial: razaoSocial.trim(),
      nomeFantasia: nomeFantasia.trim(),
      cnpj,
      inscricaoEstadual: inscricaoEstadual.trim(),
      endereco: {
        logradouro: logradouro.trim(),
        numero: numero.trim(),
        bairro: bairro.trim(),
        cidade: cidade.trim(),
        uf: uf.trim().toUpperCase(),
        cep,
      },
      contato: {
        nome: contatoNome.trim(),
        cargo: contatoCargo.trim(),
        telefone: contatoTelefone.trim(),
        email: contatoEmail.trim(),
      },
      observacoes: observacoes.trim(),
      ativo,
    });
  }

  return (
    <div className={`${styles.modalOverlay} ${styles.aberto}`}>
      <div className={styles.modal}>
        <div className={styles.modalHead}>
          <div>
            <div className={styles.modalTitle}>
              {editando ? 'Editar cliente' : 'Novo cliente'}
            </div>
            <div className={styles.modalSubtitle}>
              Preencha os dados do cliente. Campos com * são obrigatórios.
            </div>
          </div>
          <button className={styles.modalClose} onClick={onCancelar}>
            ✕
          </button>
        </div>

        <div className={styles.modalBody}>
          {/* Seção 1: Dados da empresa */}
          <div className={styles.formSection}>
            <div className={styles.formSectionTitle}>Dados da empresa</div>
            <div className={`${styles.formField} ${styles.full}`}>
              <label className={styles.formLabel}>
                Razão social <span className={styles.obrigatorio}>*</span>
              </label>
              <input
                className={styles.formInput}
                placeholder="Ex: Gerdau S/A"
                value={razaoSocial}
                onChange={(e) => setRazaoSocial(e.target.value)}
              />
            </div>
            <div
              className={`${styles.formField} ${styles.full}`}
              style={{ marginTop: 12 }}
            >
              <label className={styles.formLabel}>Nome fantasia</label>
              <input
                className={styles.formInput}
                placeholder="Ex: Gerdau · Ouro Branco"
                value={nomeFantasia}
                onChange={(e) => setNomeFantasia(e.target.value)}
              />
              <div className={styles.formHint}>
                Nome curto que aparece nas listagens e OS.
              </div>
            </div>
            <div className={styles.formGrid2} style={{ marginTop: 12 }}>
              <div className={styles.formField}>
                <label className={styles.formLabel}>
                  CNPJ <span className={styles.obrigatorio}>*</span>
                </label>
                <input
                  className={`${styles.formInput} ${styles.mono}`}
                  placeholder="00.000.000/0000-00"
                  value={cnpj}
                  onChange={(e) => setCnpj(mascaraCNPJ(e.target.value))}
                  style={!cnpjOk ? { borderColor: '#A32D2D' } : {}}
                />
                <div
                  className={styles.formHint}
                  style={!cnpjOk ? { color: '#A32D2D' } : {}}
                >
                  {!cnpjOk
                    ? 'CNPJ incompleto. Formato: 00.000.000/0000-00'
                    : 'Formato: 00.000.000/0000-00'}
                </div>
              </div>
              <div className={styles.formField}>
                <label className={styles.formLabel}>Inscrição Estadual</label>
                <input
                  className={`${styles.formInput} ${styles.mono}`}
                  placeholder="Opcional"
                  value={inscricaoEstadual}
                  onChange={(e) => setInscricaoEstadual(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Seção 2: Endereço */}
          <div className={styles.formSection}>
            <div className={styles.formSectionTitle}>Endereço</div>
            <div className={styles.formGridEndereco}>
              <div className={styles.formField}>
                <label className={styles.formLabel}>Logradouro</label>
                <input
                  className={styles.formInput}
                  placeholder="Ex: Rodovia MG-443, km 7"
                  value={logradouro}
                  onChange={(e) => setLogradouro(e.target.value)}
                />
              </div>
              <div className={styles.formField}>
                <label className={styles.formLabel}>Número</label>
                <input
                  className={styles.formInput}
                  placeholder="s/n"
                  value={numero}
                  onChange={(e) => setNumero(e.target.value)}
                />
              </div>
            </div>
            <div className={styles.formField} style={{ marginTop: 12 }}>
              <label className={styles.formLabel}>Bairro / Complemento</label>
              <input
                className={styles.formInput}
                placeholder="Ex: Distrito Industrial"
                value={bairro}
                onChange={(e) => setBairro(e.target.value)}
              />
            </div>
            <div className={styles.formGridCidade} style={{ marginTop: 12 }}>
              <div className={styles.formField}>
                <label className={styles.formLabel}>Cidade</label>
                <input
                  className={styles.formInput}
                  placeholder="Ex: Ouro Branco"
                  value={cidade}
                  onChange={(e) => setCidade(e.target.value)}
                />
              </div>
              <div className={styles.formField}>
                <label className={styles.formLabel}>UF</label>
                <input
                  className={styles.formInput}
                  placeholder="MG"
                  maxLength={2}
                  value={uf}
                  onChange={(e) => setUf(e.target.value.toUpperCase())}
                />
              </div>
              <div className={styles.formField}>
                <label className={styles.formLabel}>CEP</label>
                <input
                  className={`${styles.formInput} ${styles.mono}`}
                  placeholder="00000-000"
                  value={cep}
                  onChange={(e) => setCep(mascaraCEP(e.target.value))}
                />
              </div>
            </div>
          </div>

          {/* Seção 3: Contato técnico */}
          <div className={styles.formSection}>
            <div className={styles.formSectionTitle}>Contato técnico</div>
            <div className={styles.formGrid2}>
              <div className={styles.formField}>
                <label className={styles.formLabel}>Nome</label>
                <input
                  className={styles.formInput}
                  placeholder="Ex: Roberto Almeida"
                  value={contatoNome}
                  onChange={(e) => setContatoNome(e.target.value)}
                />
              </div>
              <div className={styles.formField}>
                <label className={styles.formLabel}>Cargo / Função</label>
                <input
                  className={styles.formInput}
                  placeholder="Ex: Eng. de Manutenção"
                  value={contatoCargo}
                  onChange={(e) => setContatoCargo(e.target.value)}
                />
              </div>
            </div>
            <div className={styles.formGrid2} style={{ marginTop: 12 }}>
              <div className={styles.formField}>
                <label className={styles.formLabel}>Telefone</label>
                <input
                  className={styles.formInput}
                  placeholder="(31) 9 9999-9999"
                  value={contatoTelefone}
                  onChange={(e) => setContatoTelefone(e.target.value)}
                />
              </div>
              <div className={styles.formField}>
                <label className={styles.formLabel}>Email</label>
                <input
                  className={styles.formInput}
                  type="email"
                  placeholder="contato@cliente.com"
                  value={contatoEmail}
                  onChange={(e) => setContatoEmail(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Seção 4: Observações */}
          <div className={styles.formSection}>
            <div className={styles.formSectionTitle}>Observações</div>
            <div className={styles.formField}>
              <textarea
                className={styles.formTextarea}
                placeholder="Notas internas: particularidades de acesso à planta, exigências de EPI, histórico..."
                value={observacoes}
                onChange={(e) => setObservacoes(e.target.value)}
              />
            </div>
          </div>

          {/* Seção 5: Status */}
          <div className={styles.formSection}>
            <div className={styles.formSectionTitle}>Status</div>
            <div className={styles.toggleStatus}>
              <div
                className={`${styles.toggleSwitch} ${ativo ? styles.ativo : ''}`}
                onClick={() => setAtivo(!ativo)}
              />
              <div className={styles.toggleLabel}>
                Cliente {ativo ? 'ativo' : 'inativo'}
                <small>Clientes inativos não aparecem ao criar novas OS.</small>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.modalFoot}>
          <button className={styles.btnSecondary} onClick={onCancelar}>
            Cancelar
          </button>
          <button
            className={styles.btnPrimary}
            onClick={handleSalvar}
            disabled={!valido}
          >
            {editando ? 'Salvar alterações' : 'Cadastrar cliente'}
          </button>
        </div>
      </div>
    </div>
  );
}
